import {
  EmailDraftT,
  LinkedConversationTypes,
  MessagetextContentType,
  Response,
  Tag,
} from 'lib-shared-legacy-types';
import {
  AdvancedSearchResult,
  ReadAdvancedSearchResultsApiArg,
  ReadAdvancedSearchResultsApiResponse,
} from 'lib-shared-types-api';

import { ajaxSend } from 'api/api';
import { LanguageCode } from 'app/utils/supportedLanguages';

import draftEmailHelper, {
  LocalStorageEmailDrafts,
} from '../helper/DraftEmailHelper';

export const base = '/v1/conversations';

export function getDrafts(): Promise<LocalStorageEmailDrafts> {
  try {
    const emailDrafts = draftEmailHelper.getDrafts();
    return Promise.resolve(emailDrafts);
  } catch (e) {
    return Promise.reject(e);
  }
}

export function removeDraft({
  timestamp,
}: EmailDraftT): Promise<LocalStorageEmailDrafts> {
  try {
    const emailDrafts = draftEmailHelper.getDrafts() || { drafts: [] };
    const filteredEmailDrafts: EmailDraftT[] = emailDrafts.drafts.filter(
      (draft) => draft.timestamp !== timestamp,
    );
    const payload: LocalStorageEmailDrafts = {
      drafts: filteredEmailDrafts,
    };
    draftEmailHelper.setDrafts(payload);
    return Promise.resolve(payload);
  } catch (e) {
    return Promise.reject(e);
  }
}

export type SaveDraftPayload = Omit<EmailDraftT, 'timestamp'>;

export function saveDraft(
  payload: SaveDraftPayload,
): Promise<LocalStorageEmailDrafts> {
  try {
    const {
      fromEmails,
      toUser,
      subject,
      message,
      internalNote,
      attachments,
      tags,
      cc,
      bcc,
      queue,
      offer,
      satisfactionLanguage,
      cancelledSatisfaction,
      parentConversation,
      linkConversationType,
      messageContentType,
    } = payload;

    const email: EmailDraftT = {
      timestamp: new Date().getTime(),
      fromEmails,
      toUser,
      cc,
      bcc,
      subject,
      message,
      internalNote,
      attachments,
      tags,
      queue,
      offer,
      satisfactionLanguage,
      cancelledSatisfaction,
      linkConversationType,
      messageContentType,
      parentConversation,
    };

    const emailDrafts: LocalStorageEmailDrafts =
      draftEmailHelper.getDrafts() || {
        drafts: [],
      };

    // Add email to drafts array.
    draftEmailHelper.setDrafts({
      drafts: [...emailDrafts.drafts, email],
    });
    return Promise.resolve(draftEmailHelper.getDrafts());
  } catch (e) {
    return Promise.reject(e);
  }
}

export type EmailAddressWithOptionalName = {
  email: string;
  name?: string;
};

export type EmailPayload = {
  from_email: string;
  to_email: string | null | undefined;
  subject: string;
  body: string;
  attachments_v2?: Array<{
    pretty_name: string;
    url: string;
  }>;
  tags?: Tag[];
  cc?: EmailAddressWithOptionalName[];
  bcc?: EmailAddressWithOptionalName[];
  content_type: MessagetextContentType;
  language?: LanguageCode;
  internalNote?: string;
  queueId?: string;
  offer?: boolean;
  parentConversation?: number;
  linkType?: LinkedConversationTypes;
};

type SendEmailPayload = Pick<
  EmailPayload,
  | 'from_email'
  | 'subject'
  | 'body'
  | 'attachments_v2'
  | 'content_type'
  | 'language'
  | 'offer'
> & {
  to_email: EmailPayload['to_email'];
  cc_emails?: EmailPayload['cc'];
  bcc_emails?: EmailPayload['bcc'];
  internal_note?: EmailPayload['internalNote'];
  queue_id?: EmailPayload['queueId'];
  tags?: string[];
  link?: {
    parent_csid: number;
    link_type?: string;
  };
};

export type SendEmailResponseT = Response<{
  csid: number;
  organization_id: string;
}>;

export function sendEmail({
  from_email,
  to_email,
  subject,
  body,
  attachments_v2,
  tags,
  cc,
  bcc,
  content_type,
  language,
  internalNote,
  offer,
  queueId,
  parentConversation,
  linkType,
}: EmailPayload): Promise<SendEmailResponseT> {
  const data: SendEmailPayload = {
    from_email,
    to_email,
    subject,
    body,
    content_type,
    ...(attachments_v2?.length ? { attachments_v2 } : {}),
    ...(cc?.length ? { cc_emails: cc } : {}),
    ...(bcc?.length ? { bcc_emails: bcc } : {}),
    ...(tags?.length ? { tags: tags.map((tag) => tag.id as string) } : {}),
    ...(language ? { language } : {}),
    ...(internalNote?.length ? { internal_note: internalNote } : {}),
    ...(offer ? { offer } : {}),
    ...(queueId ? { queue_id: queueId } : {}),
    ...(parentConversation
      ? {
          link: {
            parent_csid: parentConversation,
            ...(linkType ? { link_type: linkType } : {}),
          },
        }
      : {}),
  };

  return ajaxSend<SendEmailResponseT>({
    path: `${base}/email`,
    method: 'POST',
    data,
  });
}

export function findUser(payload: string) {
  const search = payload;
  //search?scope=user&query= &facetFilters=has_email:true
  return ajaxSend({
    path: `/v2/search?scope=user&query=${encodeURIComponent(
      search,
    )}&pageSize=10`,
  });
}

export async function findContact(
  arg: ReadAdvancedSearchResultsApiArg,
): Promise<ReadAdvancedSearchResultsApiResponse> {
  const response = await ajaxSend<Response<AdvancedSearchResult[]>>({
    path: `/v3/search/advanced/users`,
    method: 'POST',
    data: arg.params,
  });

  return {
    data: {
      results: response.data,
    },
    meta: {},
  };
}

export function getAllFrom() {
  return ajaxSend({
    path: '/v1/integrations/email',
  });
}

export function getPresignedUrl(file) {
  return ajaxSend({
    path: '/v1/uploads/presigned_url/user_avatar',
    method: 'POST',
    data: {
      pretty_name: file.name,
      content_type: file.type,
    },
  });
}

export function addAttachments(presigned, file) {
  return ajaxSend({
    method: 'PUT',
    headers: {
      authorization: null,
      contentType: file.type,
    },
    data: file,
    path: presigned.upload_url,
  });
}
